import React, {FunctionComponent} from "react";
import {AChartX, Period, Skeleton} from "@atiautomacao/ati-ui-library";
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";

interface Props {
    open: boolean;
    onClose: () => void;
    powerStationName: string;
    period: Period;
    onPeriodChange: (period: Period) => void;
    option: any;
    isLoading: boolean;
    hasContent: boolean;
}

const CHART_HEIGHT = 600;

const ActivePowerInvertersChartModal: FunctionComponent<Props> = ({
    open,
    onClose,
    powerStationName,
    period,
    onPeriodChange,
    option,
    isLoading,
    hasContent,
}) => {
    const theme = useTheme();


    const modalOption = React.useMemo(() => {
        if (!option) return option;
        const pageStyle = {
            pageButtonItemGap: 4,
            pageIconColor: theme.palette.text.primary,
            pageIconInactiveColor: theme.palette.action.disabled,
        };
        const legend = Array.isArray(option.legend)
            ? option.legend.map((lg: any) => ({
                ...lg,
                ...pageStyle,
                // preserva o rich text (legenda "Referência"); nas demais aplica a cor do tema
                textStyle: lg.textStyle?.rich
                    ? lg.textStyle
                    : {...(lg.textStyle ?? {}), color: theme.palette.text.primary},
            }))
            : {
                ...(option.legend ?? {}),
                show: true,
                type: 'scroll',
                bottom: 0,
                ...pageStyle,
                textStyle: {color: theme.palette.text.primary},
            };
        return {
            ...option,
            legend,
            grid: {
                ...(option.grid ?? {}),
                bottom: 60,
            },
        };
    }, [option, theme.palette.mode]);

    const handleDateChange = (newValue: Date | null) => {
        if (!newValue) return;
        const from = new Date(newValue);
        from.setHours(0, 0, 0, 0);
        const to = new Date(newValue);
        to.setHours(23, 59, 59, 999);
        onPeriodChange({
            ...period,
            fromDateTime: from,
            toDateTime: to,
        });
    };

    let content: JSX.Element;
    if (isLoading) {
        content = <Skeleton animation="wave" height={CHART_HEIGHT} variant="rounded" width={"100%"}/>;
    } else if (hasContent) {
        content = (
            <AChartX
                key={`active-power-modal-chart`}
                height={CHART_HEIGHT}
                option={modalOption}
                theme={theme.palette.mode}
            />
        );
    } else {
        content = <DataNotFound/>;
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            PaperProps={{sx: {borderRadius: 2}}}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{px: 3, py: 2}}
            >
                <Typography variant="h6" sx={{fontWeight: 500}}>
                    {powerStationName}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Data"
                            inputFormat="dd/MM/yyyy"
                            views={['day']}
                            value={period.toDateTime}
                            onChange={handleDateChange}
                            renderInput={(params: any) => (
                                <TextField
                                    size="small"
                                    sx={{width: 170}}
                                    {...params}
                                />
                            )}
                        />
                    </LocalizationProvider>
                    <IconButton onClick={onClose} size="small" aria-label="Fechar">
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </Stack>
            <DialogContent sx={{px: 3, pb: 3, pt: 0}}>
                <Box>{content}</Box>
            </DialogContent>
        </Dialog>
    );
};

export default ActivePowerInvertersChartModal;
